import InputAdornment from '@material-ui/core/InputAdornment';
import { FeatureFlag } from '@ps/web-core-enums';
import { generatePath } from '@ps/web-core-utils';
import { Input, StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useFeature from '@swf/core/hooks/useFeature';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { mustBeBiggerOrEqual, mustBeLowerOrEqual } from '@swf/poker';

import useJoinPublicClub from '../../hooks/api/useJoinPublicClub';
import { useHomeGamesWidget } from '../../model';
import { ClubRoute, ModalRoutes } from '../../types/common';

import {
  TXTCLI_Club_Name,
  TXTCLI_ExampleX,
  TXTCLI_Search,
  TXTWCL_Create_Public_Club,
  TXTWCL_DISCOVER_Xspan_styleXXcolorX_r_ELL,
  TXTWCL_Join,
  TXTWCL_Must_be_between_N0_and_N1_char_ELL,
  TXTWCL_N0_public_clubs_found,
  TXTWCL_Or_you_can_create_your_own_pub_ELL,
  TXTWCL_Please_adjust_your_search_crit_ELL,
  TXTWCL_Search_for_communities_and_con_ELL,
  TXTWCL_Search_results,
  TXTWCL_Something_went_wrongX_please_t_ELL,
  TXTWCL_Start_your_search_and_discover_ELL,
  TXTWCL_Visit_Here,
  TXTWCL_Your_search_did_not_match_any__ELL
} from './PublicClubs.messages.json';
import styles from './PublicClubs.scss';
import BgImageIcon from './assets/bgimage.svg';
import IconEarth from './assets/icon-earth.svg';
import SearchInputIcon from './assets/search-input.svg';
import SearchIcon from './assets/search.svg';
import usePublicClubsRequest from './hooks/usePublicClubsRequest';

const PublicClubs: React.FC = () => {
  const { modal } = useDispatch<Dispatch>();
  const [loading, setLoading] = React.useState(false);
  const { formatMessage } = useIntl();
  const isPublicClubsEnabled = useFeature(FeatureFlag.PKRHomeGamesPublicClubSearch);
  const { widgetId } = useWidget();
  const userId = useSelector(select.account.getUserId) || '';
  const { widgetDispatch, widgetSelect } = useHomeGamesWidget();
  const clubIds = useSelector(widgetSelect.joinedOrOwnedClubsIds);

  const joinPublicClub = useJoinPublicClub();
  const { handleSubmit, publicClubs, isEmptyResult } = usePublicClubsRequest({ setLoading });

  const displayLandingScreen = !isEmptyResult && publicClubs.length === 0;
  const displayEmptyClubsScreen = isEmptyResult && publicClubs.length === 0;
  const searchPlaceholder = formatMessage(TXTCLI_Club_Name);
  const { push } = useHistory();
  const aliases = useSelector(select.cms.getAliases);

  const handleOpenClub = (clubId?: number) => {
    if (clubId && aliases) {
      push(
        `${generatePath(aliases.homeGamesClubs as string, {
          clubId,
          section: ClubRoute.Lobby
        })}`
      );
    }
  };
  const handleJoin = async (clubId?: number) => {
    if (clubId) {
      try {
        setLoading(true);
        const { error } = await joinPublicClub(clubId, userId);

        if (error) {
          modal.enqueue({
            path: ModalRoutes.notification,
            locationState: { text: error }
          });
        } else {
          await widgetDispatch.fetchLobbyData({});
        }

        setLoading(false);
      } catch {
        modal.enqueue({
          path: ModalRoutes.notification,
          locationState: { text: <Translation {...TXTWCL_Something_went_wrongX_please_t_ELL} /> }
        });
        setLoading(false);
      }
    }
  };

  if (!isPublicClubsEnabled) return null;

  return (
    <>
      <div className={styles.search}>
        <div className={styles.discoverWrapper}>
          <IconEarth width="46" height="46" />
          <div className={styles.discover}>
            <h2>
              <Translation {...TXTWCL_DISCOVER_Xspan_styleXXcolorX_r_ELL} />
            </h2>
            <p className={styles.description}>
              <Translation {...TXTWCL_Search_for_communities_and_con_ELL} />
            </p>
          </div>
          <BgImageIcon className={styles.bgImage} />
        </div>
        <Form
          onSubmit={handleSubmit}
          render={(props) => (
            <form className={styles.form} onSubmit={props.handleSubmit}>
              <Input
                id="search-clubs"
                className={styles.searchInput}
                placeholder={searchPlaceholder}
                label=""
                showValidationTick={false}
                validators={[
                  mustBeBiggerOrEqual(3, {
                    ...TXTWCL_Must_be_between_N0_and_N1_char_ELL,
                    values: { 0: '3', 1: '30' }
                  }),
                  mustBeLowerOrEqual(30, {
                    ...TXTWCL_Must_be_between_N0_and_N1_char_ELL,
                    values: { 0: '3', 1: '30' }
                  })
                ]}
                icon={
                  <InputAdornment position="start">
                    <SearchInputIcon
                      className={styles.searchIcon}
                      width="24"
                      height="24"
                      stroke="white"
                    />
                  </InputAdornment>
                }
              />
              <StarsButton
                className={classNames(styles.searchBtn, styles.btnBorder)}
                block
                disabled={loading}
                translation={TXTCLI_Search}
                variant="casino"
                type="submit"
              />
            </form>
          )}
        />
        <span className={styles.example}>
          <Translation {...TXTCLI_ExampleX} /> Diners Club
        </span>
      </div>
      {displayLandingScreen && (
        <div className={styles.clubsEmpty}>
          <div>
            <SearchIcon />
          </div>
          <h3>
            <Translation {...TXTWCL_Start_your_search_and_discover_ELL} />
          </h3>
          <div className={styles.separator} />
          <p className={styles.subText}>
            <Translation {...TXTWCL_Or_you_can_create_your_own_pub_ELL} />
          </p>
          <StarsButton
            className={classNames(styles.createBtn, styles.btnBorder)}
            block
            disabled={loading}
            onClick={() => {
              modal.enqueue({
                path: ModalRoutes.createClub,
                locationState: { widgetId, isPublic: true }
              });
            }}
            translation={TXTWCL_Create_Public_Club}
            variant="casino"
            type="submit"
          />
        </div>
      )}
      {displayEmptyClubsScreen && (
        <div className={styles.clubsEmpty}>
          <div>
            <SearchIcon />
          </div>
          <h3>
            <Translation {...TXTWCL_Your_search_did_not_match_any__ELL} />
          </h3>
          <p className={styles.subText}>
            <Translation {...TXTWCL_Please_adjust_your_search_crit_ELL} />
          </p>
          <div className={styles.separator} />
          <p className={styles.subText}>
            <Translation {...TXTWCL_Or_you_can_create_your_own_pub_ELL} />
          </p>
          <StarsButton
            className={classNames(styles.createBtn, styles.btnBorder)}
            block
            disabled={loading}
            onClick={() => {
              modal.enqueue({
                path: ModalRoutes.createClub,
                locationState: { widgetId, isPublic: true }
              });
            }}
            translation={TXTWCL_Create_Public_Club}
            variant="casino"
            type="submit"
          />
        </div>
      )}
      {publicClubs.length > 0 && (
        <div className={styles.clubs}>
          <p className={styles.clubsResult}>
            <Translation {...TXTWCL_Search_results} />:
            <span className={styles.clubsCount}>
              <Translation {...TXTWCL_N0_public_clubs_found} values={{ 0: publicClubs.length }} />
            </span>
          </p>
          {publicClubs.map((c) => {
            const isJoined = clubIds.includes(c.clubId);

            return (
              <div key={c.clubId} className={styles.publicClubContainer}>
                <div className={styles.publicClubInfo}>
                  <span className={styles.clubName}>{c.clubName}</span>
                  <span className={styles.clubId}>ID: {c.clubId}</span>
                </div>
                {!isJoined ? (
                  <StarsButton
                    className={styles.joinBtn}
                    onClick={() => handleJoin(c.clubId)}
                    block
                    disabled={loading}
                    translation={TXTWCL_Join}
                    variant="casino"
                    type="submit"
                  />
                ) : (
                  <StarsButton
                    className={styles.joinBtn}
                    onClick={() => handleOpenClub(c.clubId)}
                    block
                    disabled={loading}
                    translation={TXTWCL_Visit_Here}
                    variant="primary"
                    type="submit"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default PublicClubs;
